import { useState, useEffect } from "react";
import { AlertTriangle, Eye, EyeOff, Lock, Mail, ArrowLeft } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import { useComingSoon } from "../utils/useComingSoon";


import { useUserAuth } from "../services/user/UserAuthServices";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { userLogin, loading, error, clearError } = useUserAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showComingSoon } = useComingSoon();


  const searchParams = new URLSearchParams(location.search);
  const reason = searchParams.get("reason") || null;
  const isSessionExpired = reason === "session_expired";
  const nextPath = searchParams.get("next") || "/dashboard";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showNotification({
        type: "warning",
        title: "Missing Fields",
        message: "Please fill in all required fields."
      });
      return;
    }

    const result = await userLogin(email, password, nextPath);
    if (result.status === "success") {
      navigate(nextPath);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212] flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center p-4 py-12 md:py-20 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#62397f]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="w-full max-w-md relative animate-fade-in">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] hover:underline mb-8 font-medium transition-all group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Home</span>
          </Link>

          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#62397f] to-[#7C3AED] flex items-center justify-center shadow-xl mx-auto mb-4 transform hover:scale-105 transition-transform duration-300">
              <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" className="w-16 h-16 object-contain" />
            </div>
            <h1 className="text-3xl font-medium font-serif text-[#111111] dark:text-white mb-2">
              Welcome Back
            </h1>
            <p className="text-[#666666] dark:text-[#A0A0A0]">
              Sign in to manage your rentals and account
            </p>
          </div>

          {/* Session Banner */}
          {isSessionExpired && (
            <div className="mb-6 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl px-4 py-3 animate-fade-in">
              <AlertTriangle className="text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" size={18} />
              <div>
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Session Expired</p>
                <p className="text-sm text-amber-600 dark:text-amber-400">Please sign in again to continue.</p>
              </div>
            </div>
          )}

          {/* Login Card */}
          <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                  <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#111111] dark:text-white ml-1">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-[#111111] dark:text-white placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 focus:border-[#62397f] transition-all"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#111111] dark:text-white ml-1">
                  Password
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-[#111111] dark:text-white placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 focus:border-[#62397f] transition-all"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#62397f] transition-colors p-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="flex items-center justify-between px-1">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-[#E5E7EB] dark:border-[#2A2A2A] text-[#62397f] focus:ring-[#62397f] bg-[#F9FAFB] dark:bg-[#0A0A0A]"
                  />
                  <span className="text-sm text-[#666666] dark:text-[#A0A0A0] group-hover:text-[#111111] dark:group-hover:text-white transition-colors">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#62397f] dark:text-[#A78BFA] hover:underline font-semibold"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-[#F0F0F0] dark:border-[#2A2A2A]"></span>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-[#1A1A1A] text-[#999999]">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => showComingSoon({
                  title: "Google Login",
                  message: "Google authentication is being integrated and will be available as a secure login option soon."
                })}
                className="w-full py-4 px-6 bg-white dark:bg-transparent border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white font-semibold rounded-2xl flex items-center justify-center gap-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-all shadow-sm group"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                <span>Sign in with Google</span>
              </button>
            </form>


            {/* Registration Promise */}
            <div className="mt-8 pt-8 border-t border-[#F0F0F0] dark:border-[#2A2A2A] text-center">
              <p className="text-[#666666] dark:text-[#A0A0A0] text-sm">
                Don't have an account yet?{" "}
                <Link
                  to="/contact"
                  className="block sm:inline text-[#62397f] dark:text-[#A78BFA] font-bold hover:underline mt-1 sm:mt-0"
                >
                  Contact us to register
                </Link>
              </p>
            </div>
          </div>


          <div className="flex flex-col items-center justify-center mt-8 gap-3">
            <p className="text-center text-xs text-[#666666] dark:text-[#A0A0A0] leading-relaxed">
              © {new Date().getFullYear()} St. Stephen's Rentals. All rights
              reserved.
            </p>
            <div className="flex items-center justify-center text-xs text-[#666666] dark:text-[#A0A0A0] gap-4 md:gap-6">
              <Link
                to="/privacy"
                className="text-[#A0A0A0] hover:text-[#62397f] text-xs md:text-sm transition-colors duration-150"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-[#A0A0A0] hover:text-[#62397f] text-xs md:text-sm transition-colors duration-150"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </main>

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}
